import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { PurchaseStatus } from "@/app/payments/types";
import { Button } from "@/components/Button";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { BookingError } from "@/components/Errors/BookingError";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { OTPPopup } from "@/components/OTPPopup/OTPPopup";
import { PaymentChooser } from "@/components/PaymentChooser/PaymentChooser";
import {
  PaymentDetailsMonthlyPaymentRow,
  PaymentDetailsTotalPriceRow,
} from "@/components/PaymentDetails";
import { PriceChangedModal } from "@/components/PriceChangedModal/PriceChangedModal";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { SearchDataMissingError } from "@/errors/SearchDataMissingError";
import { useFlightFares } from "@/features/flights/components/flight-fares/useFlightFares";
import { FlightOfferError } from "@/features/flights/components/flight-purchase/FlightOfferError";
import { FlightRouteAndDate } from "@/features/flights/components/flight-purchase/FlightRouteAndDate";
import { PaymentSummaryCard } from "@/features/flights/components/PaymentSummaryCard";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import {
  flightAvailabilityQueryOptions,
  useFlightAvailabilityMutation,
} from "@/features/flights/hooks/useFlightAvailability";
import { useFlightBooking } from "@/features/flights/hooks/useFlightBooking";
import { getFlightOrderPrices } from "@/features/flights/utils/getFlightOrderPrices";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { transformFlightTraveler } from "@/features/travellers/utils/transformTraveler";
import { useCreatePendingInstallment } from "@/hooks/useCreatePendingInstallment";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import {
  BnplTrackingEvents,
  FlightTrackingEvents,
  rudderAnalytics,
} from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/flights/_offers/offer/$id/payment",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const {
    paymentType,
    sessionId,
    isEligible,
    refreshSession,
    selectedInstallments,
  } = usePaymentContext();
  const { updatePurchaseState, purchaseState } = usePurchaseStateContext();
  const { selectedFlightPayload, promoCode } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );
  const { selectedTravellers } = useTravellersSelectionContext();
  const { selectedFare } = useFlightFares();
  const [isContinuing, setIsContinuing] = useState(false);

  if (!selectedFlightPayload) {
    throw new SearchDataMissingError("Flight search data is missing");
  }

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const areAdditionalServicesEnabled = useFeatureIsOn(
    "additional-services-selection",
  );
  const arePromoCodesEnabled = useFeatureIsOn("promo-codes");

  const {
    data: promoCodeData,
    validationStatus: promoCodeValidationStatus,
    revalidate,
  } = usePromoCodeValidation({
    service: "flights",
    validationRequestData: {
      promoCode: promoCode || "",
      availabilityToken: singleFlightDetails?.availabilityToken || "",
    },
  });

  const paymentMethod = paymentType === "bnpl" ? "bnpl" : "qiAccount";

  const { mutateAsync: createPendingInstallment } =
    useCreatePendingInstallment();

  const { qiPayment, checkoutPayment, bnplPayment } = useFlightBooking({
    method: paymentMethod,
    initialAvailabilityToken: singleFlightDetails?.availabilityToken,
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        if (paymentType === "bnpl") {
          // to update eligibility info
          refreshSession();
        }

        navigate({
          to: "/flights/offer/$id/confirmation",
          search: { orderId },
        }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const {
    data: availability,
    isError: isAvailabilityError,
    error: availabilityError,
    isPending: isAvailabilityRefetching,
    mutate: refetchAvailability,
  } = useFlightAvailabilityMutation({
    onSuccess: (data) => {
      if (data.data) {
        const latestTotalPrice = data.data.totalPrice;
        if (latestTotalPrice !== basePrice) {
          revalidate();
          setShowConfirmation(true);
        } else {
          handlePurchase(singleFlightDetails?.availabilityToken);
        }
      }
    },
  });

  const promoCodeToApply = promoCodeData?.result
    ? promoCode || undefined
    : undefined;

  const createBookingPayload = (availabilityToken: string) => {
    return {
      promoCode: promoCodeToApply,
      offerId: selectedFlightPayload.index,
      searchResultKey: selectedFlightPayload.searchResultKey,
      searchResultToken: selectedFlightPayload.searchResultToken,
      itineraryIndex: selectedFlightPayload.itineraryIndex,
      availabilityToken,
      travelers: selectedTravellers.map((traveller) =>
        transformFlightTraveler(traveller),
      ),
    };
  };

  const handlePurchase = async (availabilityToken?: string) => {
    if (
      !selectedFlightPayload ||
      purchaseState === "completed" ||
      !availabilityToken
    ) {
      return;
    }

    setIsContinuing(true);

    try {
      if (paymentMethod === "bnpl") {
        const { data: eligibilityData } = await refreshSession();
        if (eligibilityData?.isEligible) {
          await createPendingInstallment({
            sessionId: eligibilityData.sessionId,
            amount: basePrice,
            countOfMonth: selectedInstallments,
          });

          setShowOTP(true);
        }

        return;
      }

      const payload = createBookingPayload(availabilityToken);
      if (!payload) return;

      rudderAnalytics.track(
        FlightTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN,
        {
          amount: totalPrice,
          payment_method: paymentMethod,
          currency: singleFlightDetails?.currency,
        },
      );

      if (paymentMethod === "qiAccount") {
        await qiPayment.mutateAsync(payload);
      } else {
        await checkoutPayment.submit(payload);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsContinuing(false);
    }
  };

  if (isAvailabilityError)
    return <FlightOfferError error={availabilityError} />;

  if (!singleFlightDetails) return null;

  if (qiPayment.isError) {
    return <BookingError error={qiPayment.error} type="flights" />;
  }

  if (
    qiPayment.isPending ||
    checkoutPayment.isPending ||
    bnplPayment.isPending
  ) {
    return (
      <div className="h-screen">
        <PageLoader>
          <AnimatedLoader icon="airplane" />
          <h3 className="mt-3 font-normal text-text-primary">
            {intl.formatMessage({
              defaultMessage: "We are booking your flight...",
              description:
                "Loading message shown while the flight booking is being processed",
            })}
          </h3>
        </PageLoader>
      </div>
    );
  }

  const {
    basePrice,
    baseTicketPrice,
    servicesPrice,
    farePrice,
    totalPrice,
    promoCodeDiscountAmount,
  } = getFlightOrderPrices(singleFlightDetails, selectedFare, promoCodeData);

  return (
    <div className="px-main">
      <div className="mb-4 text-xl font-medium">
        {intl.formatMessage({
          defaultMessage: "Payment method",
          description: "Page heading for the flight payment page",
        })}
      </div>

      <div className="flex w-full flex-col gap-2.5">
        <div className="section-card">
          <ul className="space-y-4">
            {singleFlightDetails?.flights.map((flight, index) => (
              <li key={index}>
                <FlightRouteAndDate
                  flightDetails={flight}
                  segmentDirection={index === 0 ? "departure" : "return"}
                />
              </li>
            ))}
          </ul>
        </div>

        <PaymentChooser
          price={totalPrice}
          onChange={(paymentType) => {
            const eventName =
              paymentType === "bnpl"
                ? BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
                : BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT;

            rudderAnalytics.track(eventName, {
              is_bnpl_eligible: isEligible,
            });
          }}
        />

        <PaymentSummaryCard
          basePrice={baseTicketPrice}
          brandedFarePrice={farePrice === 0 ? undefined : farePrice}
          servicesPrice={
            areAdditionalServicesEnabled ? servicesPrice : undefined
          }
          promoCodeDiscount={
            arePromoCodesEnabled ? promoCodeDiscountAmount : undefined
          }
          summary={
            paymentType === "bnpl" ? (
              <PaymentDetailsMonthlyPaymentRow
                totalPrice={totalPrice}
                selectedInstallments={selectedInstallments}
              />
            ) : (
              <PaymentDetailsTotalPriceRow totalPrice={totalPrice} />
            )
          }
        />
      </div>

      {checkoutPayment.isLoadingCheckoutForm ? (
        <CircularProgress size={20} />
      ) : null}
      <div id="flow-container" className="section-card p-0 not-empty:mt-2" />

      <PriceChangedModal
        open={showConfirmation}
        price={formatCurrency(availability?.data.data.totalPrice)}
        onCancel={() => {
          setShowConfirmation(false);
          navigate({ to: "/flights/search" });
        }}
        onConfirm={() => {
          handlePurchase(availability?.data.data.availabilityToken);
        }}
      />
      <OTPPopup
        isOpen={showOTP}
        onClose={() => {
          setShowOTP(false);
        }}
        onSubmit={(otp) => {
          if (!sessionId || !singleFlightDetails?.availabilityToken) {
            return;
          }

          const payload = createBookingPayload(
            singleFlightDetails.availabilityToken,
          );

          bnplPayment.mutate({
            ...payload,
            sessionId,
            otp,
          });
        }}
        isSubmitting={bnplPayment.isPending}
        error={
          bnplPayment.error
            ? intl.formatMessage({
                defaultMessage: "OTP verification failed",
                description: "Error message shown when OTP verification fails",
              })
            : undefined
        }
      />

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            refetchAvailability(selectedFlightPayload);
          }}
          disabled={promoCodeValidationStatus?.type === "loading"}
          loading={isAvailabilityRefetching || isContinuing}
        >
          {intl.formatMessage({
            defaultMessage: "Book and Pay",
            description:
              "Label for the button that confirms the flight booking and proceeds to payment",
          })}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
